import { apiErrorMessage } from '@agos/api-client'
import {
  authRoleFields,
  baseSentence,
  effectiveRoleKey,
  formatCount,
  isRoleBase,
  isRoleColor,
  roleColorLabel,
  roleDisplayName,
  roleLabel,
  STAFF_ROLE_COLORS,
  T,
  tt,
  useAuth,
  useMenuPreview,
  type RoleColor,
} from '@agos/shared'
import {
  BUTTON_PRIMARY,
  cn,
  ConfirmDialog,
  FilterBar,
  StatCard,
  StatusBadge,
  TabGroup,
  useToast,
} from '@agos/ui'
import { keepPreviousData } from '@tanstack/react-query'
import { AlertCircle, AlertTriangle, ArrowLeft, ChevronRight, Lock, Server, Shield, Users } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import {
  roleRefusalCode,
  usePreviewRolePermissions,
  useReplaceRolePermissions,
  useUpdateRole,
  type RolesOverview,
  type RoleView,
} from '../../roles/roles-api'
import {
  childrenOf,
  displayChildrenOf,
  displayParentOf,
  effectiveCount,
  grantedCount,
  grantedViaParent,
  groupCatalogue,
  isDirty,
  matchesSearch,
  parentOf,
  permissionDescription,
  permissionLabel,
  roleDiff,
  rolePayload,
  switchState,
  toggled,
  type SwitchState,
} from '../permission-matrix'
import {
  inRightsScope,
  lockText,
  mayEditRights,
  membersOf,
  nextTicks,
  previewOf,
  rightLock,
  roleMetaPatch,
  roleNameProblem,
  roleNameProblemText,
  roleWarnings,
  savingReachText,
  ticksVersion,
  toggleGroup,
  type Navigate,
  type RightLock,
  type RightsScope,
  type RoleTicks,
} from '../role-model'
import { RoleChip } from '../RoleChip'
import { PreviewRail, type PreviewState } from './PreviewRail'

/**
 * A role's editor - `?role=key` on the *Role* tab, `FRONTEND.md` §9.752 (design §2.9).
 *
 * **FB-103's matrix, kept whole**: the four figures, the third switch state (*held through the parent*), the side
 * effects of a parent change, the collapsed groups and the search that opens them (§9.176, §9.145) - every marker
 * `permissions.spec.ts` reads is here under its old name. What O119 adds is around it:
 *
 * - the base's sentence, written from the code's own facts (`baseSentence`), and the two traps where they are true;
 * - on every right, *Na obrazovke*: the sidebar entry it opens, from the catalogue the shell lends (`useMenuPreview`);
 * - *Všetko / Len zapnuté / Len zmenené*;
 * - a ***server kontroluje*** mark on every right the server checks at all - the rest move the menu and the screen;
 * - for a delegate, a lock on every right they may not move, with its reason (§10A.408's R8);
 * - the rail: *Zmeny* and the server's *Koho sa to týka*, and **Uložiť zmeny** confirming *Zmení sa pre N ľudí*;
 * - a save that sends the `updatedAt` the ticks were made on (`RoleTicks`), so somebody else's save in between is a 409
 *   `ROLE_STALE` and a reload rather than an overwrite - whatever the overview has re-read since the first tick; and
 *   when a re-read already shows the rights moved, the editor says so and offers the reload before the save.
 *
 * The rights save is `PUT /settings/roles/{key}/permissions` for every role, the built-ins included: it is the one
 * that carries the version and the delegate's rules. The old `PUT /settings/permissions/{role}` stays for old bundles.
 */
export function RoleEditor({
  overview,
  role,
  roles,
  mainAdmin,
  local,
  setLocal,
  locals,
  renaming,
  setRenaming,
  navigate,
  onDuplicate,
  onDelete,
  reload,
}: {
  overview: RolesOverview
  role: RoleView
  roles: readonly RoleView[]
  mainAdmin: boolean
  /** This role's unsaved ticks, with the version they were ticked on. */
  local: RoleTicks | undefined
  setLocal: (next: RoleTicks | null) => void
  /** Every role's unsaved ticks, for the switcher's counts. */
  locals: Readonly<Record<string, RoleTicks>>
  renaming: boolean
  setRenaming: (on: boolean) => void
  navigate: Navigate
  onDuplicate: () => void
  onDelete: () => void
  reload: () => Promise<unknown>
}) {
  const { refreshPermissions, user } = useAuth()
  const { screensOf } = useMenuPreview()
  const ownRole = effectiveRoleKey({ systemRole: user?.systemRole, roleKey: authRoleFields(user).roleKey })
  const { toast } = useToast()
  const catalogue = overview.allPermissions
  const known = useMemo(() => new Set(catalogue), [catalogue])
  const stored = useMemo(() => new Set(role.permissions), [role.permissions])
  const granted = local?.set ?? stored
  /* The version the ticks were made on, and whether the rights moved under them since - a re-read in between must not
     rebase a replacement set onto somebody else's save (§10A.408's ROLE_STALE). */
  const version = local === undefined ? null : ticksVersion(local, role)
  const rightsMoved = version?.rightsMoved === true
  const diff = roleDiff(stored, granted, catalogue)
  const viaParent = grantedViaParent(granted, catalogue)
  const groups = useMemo(() => groupCatalogue(catalogue), [catalogue])
  const serverChecked = useMemo(() => new Set(overview.serverChecked), [overview.serverChecked])
  const editable = mayEditRights(role, mainAdmin)
  const lockContext = {
    mainAdmin,
    grantable: new Set(overview.you.grantable),
    mainAdminGrants: new Set(overview.mainAdminGrants),
    stored,
  }
  const lockOf = (key: string): RightLock | null => rightLock(key, lockContext)
  /* A proposal equal to what is stored is no proposal: dropped, so the card stops saying *Neuložené zmeny* about a
     role somebody ticked and unticked. The first tick fixes the version every later save of these ticks sends. */
  const propose = (next: ReadonlySet<string>) => setLocal(nextTicks(local, next, role))
  const isLocked = (key: string) => !editable || lockOf(key) !== null

  const [search, setSearch] = useState('')
  const [scope, setScope] = useState<RightsScope>('all')
  // §9.176 - which groups the operator has opened. Collapsed is the default, so the set holds the exceptions rather
  // than the rule: an empty set is the screen as it first draws.
  const [opened, setOpened] = useState<ReadonlySet<string>>(new Set())
  const [confirming, setConfirming] = useState(false)
  const [stale, setStale] = useState(false)

  /* FB-822, §9.543's belt, carried: the rail - and with it the save - exists only while something changed, and the
     editor is never drawn over a failed read (`RolesTab` returns its failure block first). A catalogue that came back
     empty is the one way `rolePayload` can still send `{permissions: []}`, so it counts as nothing to save. */
  const dirty = editable && catalogue.length > 0 && isDirty(diff)
  const proposal = rolePayload(granted, catalogue).permissions
  const proposalKey = [...proposal].sort().join(',')
  const settledKey = useSettled(proposalKey, 400)
  const preview = usePreviewRolePermissions(
    role.key,
    { permissions: settledKey === '' ? [] : settledKey.split(',') },
    { query: { enabled: dirty, placeholderData: keepPreviousData, retry: false } },
  )
  const answer = preview.data === undefined ? null : previewOf(preview.data)
  const previewState: PreviewState = preview.isError
    ? 'failed'
    : answer !== null && settledKey === proposalKey && !preview.isPlaceholderData
      ? 'ready'
      : 'loading'
  const refused = previewState === 'ready' ? (answer?.refused ?? []) : []

  const members = membersOf(role.key, overview.people)
  const base = isRoleBase(role.baseRole) ? role.baseRole : null
  const name = roleDisplayName(role)

  const labelOf = (key: string): string => {
    const label = permissionLabel(key, displayParentOf(key, known))
    return label === key ? key : `${label} (${key})`
  }

  const save = useReplaceRolePermissions()
  const confirmSave = () =>
    save.mutate(
      { key: role.key, data: { permissions: proposal, expectedUpdatedAt: version?.expectedUpdatedAt ?? role.updatedAt } },
      {
        onSuccess: () => {
          setConfirming(false)
          setLocal(null)
          // The editor's own session holds the old set until the context polls again - three minutes, or the next
          // focus. A role somebody edits for themselves reads as a failed save if the sidebar does not move, so the
          // session is refreshed here rather than waited for (the decision `permissions.spec.ts` records).
          void refreshPermissions()
          toast({
            title: `${tt('Uložené pre rolu')} ${name}`,
            description:
              ownRole === role.key
                ? tt('Vaše vlastné oprávnenia sa práve obnovili. Ostatní prihlásení to uvidia do troch minút.')
                : tt('Prihlásení zamestnanci to uvidia do troch minút alebo po prepnutí na okno.'),
          })
        },
        onError: (error: unknown) => {
          setConfirming(false)
          // Somebody saved this role since it was read: offered a reload rather than an overwrite (§10A.408).
          if (roleRefusalCode(error) === 'ROLE_STALE') {
            setStale(true)
            return
          }
          toast({
            title: tt('Chyba'),
            description: apiErrorMessage(error, tt('Práva sa neuložili.')),
            variant: 'destructive',
          })
        },
      },
    )

  const blocked = blockedReason(editable, rightsMoved, refused.length)

  const confirmText = [
    role.builtIn && proposal.length === 0
      ? tt('Rola zostane bez práv a server ju odporúčanými už nedoplní.')
      : null,
    diff.sideEffects.length > 0
      ? `${tt('Zmení sa aj')} ${diff.sideEffects.length} ${tt('podradených práv, ktoré udeľuje nadradené právo.')}`
      : null,
    savingReachText(previewState === 'ready' && answer !== null ? answer.affected.length : null, role.memberCount),
  ]
    .filter((part): part is string => part !== null)
    .join(' ')

  const switchable = roles.filter((candidate) => candidate.key !== 'main_admin')

  return (
    <div className="space-y-4" data-role-editor={role.key} data-role-editable={editable}>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => navigate({ role: '' })}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50"
          data-role-back
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          {tt('Všetky role')}
        </button>
      </div>

      {/* The roles one may move between without going back to the cards, each with its count of ticks - the old
          matrix's role tabs, kept so a role is still one press from another. */}
      <TabGroup
        tabs={switchable.map((candidate) => ({
          id: candidate.key,
          label: `${roleDisplayName(candidate)} (${grantedCount(locals[candidate.key]?.set ?? new Set(candidate.permissions), catalogue)})`,
        }))}
        active={role.key}
        onChange={(key) => navigate({ role: key })}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_20rem] gap-4 items-start">
        <div className="space-y-4 min-w-0 max-lg:pb-28">
          <div className="rounded-2xl border border-border bg-card p-4 space-y-2" data-role-header>
            <div className="flex flex-wrap items-center gap-2">
              <RoleChip roleKey={role.key} roles={roles} size="md" />
              <StatusBadge
                label={role.builtIn ? tt('Vstavaná') : tt('Vlastná')}
                color={role.builtIn ? 'zinc' : 'violet'}
              />
              {!editable && <StatusBadge label={tt('Len na čítanie')} color="zinc" icon={Lock} />}
              {mainAdmin && (
                <div className="flex items-center gap-1.5 ml-auto">
                  {!role.builtIn && !renaming && (
                    <button
                      type="button"
                      onClick={() => setRenaming(true)}
                      className="px-2.5 py-1 rounded-lg border border-border text-[11px] font-semibold hover:bg-muted/50"
                      data-role-meta-open
                    >
                      {tt('Upraviť názov a farbu')}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={onDuplicate}
                    className="px-2.5 py-1 rounded-lg border border-border text-[11px] font-semibold hover:bg-muted/50"
                    data-role-duplicate={role.key}
                  >
                    {tt('Duplikovať')}
                  </button>
                  {!role.builtIn && (
                    <button
                      type="button"
                      onClick={onDelete}
                      className="px-2.5 py-1 rounded-lg border border-red-500/40 text-red-500 text-[11px] font-semibold hover:bg-red-500/10"
                      data-role-delete={role.key}
                    >
                      {tt('Zmazať')}
                    </button>
                  )}
                </div>
              )}
            </div>

            {renaming && mainAdmin && !role.builtIn && (
              <RoleMetaForm
                key={role.key}
                role={role}
                roles={roles}
                onDone={() => setRenaming(false)}
              />
            )}

            {!renaming && (role.description ?? '').trim() !== '' && (
              <p className="text-xs text-muted-foreground" data-role-description>
                {role.description}
              </p>
            )}

            {base !== null && (
              <p className="text-xs" data-role-base-sentence={base}>
                <strong>
                  {tt('Pracuje ako')}: {roleLabel(base)}.
                </strong>{' '}
                <span className="text-muted-foreground">{baseSentence(base, overview.roleListReach[base])}</span>
              </p>
            )}

            {roleWarnings(role, overview.roleListReach).map((warning) => (
              <p
                key={warning.kind}
                className="flex items-start gap-1.5 text-xs text-amber-500"
                data-role-warning={warning.kind}
              >
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                <span>{warning.text}</span>
              </p>
            ))}

            <div className="flex flex-wrap items-center gap-1.5 pt-1" data-role-member-chips={members.length}>
              <span className="text-[11px] text-muted-foreground">{tt('Ľudia s touto rolou')}:</span>
              {members.length === 0 && <span className="text-[11px] text-muted-foreground">{tt('nikto')}</span>}
              {members.map((person) => (
                <button
                  key={person.id}
                  type="button"
                  onClick={() => navigate({ tab: 'people', person: person.id })}
                  className="px-2 py-0.5 rounded-full border border-border text-[11px] hover:bg-muted/50"
                  data-role-member={person.id}
                >
                  {person.name}
                </button>
              ))}
            </div>
          </div>

          {(stale || rightsMoved) && (
            <div
              className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3"
              data-role-stale={stale ? 'refused' : 'read'}
            >
              <p className="text-xs text-amber-500 flex-1">
                {tt('Rolu medzitým niekto zmenil - načítajte ju znova. Vaše neuložené zmeny sa zahodia.')}
              </p>
              <button
                type="button"
                onClick={() =>
                  void reload().then(() => {
                    setLocal(null)
                    setStale(false)
                  })
                }
                className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50"
                data-role-reload
              >
                {tt('Načítať znova')}
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-role-totals>
            <StatCard label={tt('Zapnuté')} value={formatCount(grantedCount(granted, catalogue))} icon={Shield} color="blue" />
            <StatCard
              label={tt('Skutočne platí')}
              value={formatCount(effectiveCount(granted, catalogue))}
              icon={Shield}
              color="violet"
            />
            <StatCard
              label={tt('Cez nadradené')}
              value={formatCount(viaParent.length)}
              icon={AlertCircle}
              color={viaParent.length > 0 ? 'amber' : 'emerald'}
            />
            <StatCard label={tt('V katalógu')} value={formatCount(catalogue.length)} icon={Users} color="emerald" />
          </div>

          {/* FB-103 - the switches that read off while the access is granted. */}
          {viaParent.length > 0 && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 space-y-1" data-via-parent>
              <p className="text-xs font-bold text-amber-500">
                <strong>{viaParent.length}</strong>{' '}
                <T>práv je vypnutých na prepínači a napriek tomu platia, pretože ich udeľuje nadradené právo.</T>
              </p>
              <p className="text-xs text-amber-500">
                {viaParent.map((permission) => `${permission} ← ${parentOf(permission, known) ?? ''}`).join(' · ')}
              </p>
            </div>
          )}

          <FilterBar
            search={{ value: search, onChange: setSearch, placeholder: tt('Hľadať právo...') }}
            actions={
              <div className="flex flex-wrap items-center gap-1" data-rights-scope-bar>
                {(
                  [
                    ['all', tt('Všetko')],
                    ['on', tt('Len zapnuté')],
                    ['changed', tt('Len zmenené')],
                  ] as const
                ).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setScope(id)}
                    data-rights-scope={id}
                    data-rights-scope-active={scope === id}
                    aria-pressed={scope === id}
                    className={cn(
                      'px-2.5 py-1.5 rounded-lg border text-[11px] font-bold transition-colors',
                      scope === id
                        ? 'bg-primary/10 border-primary text-primary'
                        : 'border-border text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            }
          />

          {mainAdmin && editable && base !== null && overview.defaults[base] !== undefined && (
            <button
              type="button"
              onClick={() => propose(new Set(overview.defaults[base]))}
              className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50"
              data-role-defaults={base}
            >
              {tt('Načítať odporúčané pre základ')} {roleLabel(base)}
            </button>
          )}

          <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground" data-server-checked-note>
            <Server className="w-3.5 h-3.5 shrink-0 mt-px" />
            <span>
              {tt(
                'Práva bez značky server nekontroluje - menia menu a obrazovku; čo obrazovka načíta, rozhoduje základ roly.',
              )}
            </span>
          </p>

          <div className="space-y-3" data-permission-groups>
            {groups.map((group) => {
              const visible = group.entries.filter(
                (entry) =>
                  // §9.145 - searching for *Vydané faktúry* must find `outgoing_invoices`.
                  matchesSearch(entry.permission, permissionLabel(entry.permission, entry.parent), search) &&
                  inRightsScope(entry.permission, scope, granted, stored),
              )
              if (visible.length === 0) return null
              const keys = visible.map((entry) => entry.permission)
              const { allOn } = toggleGroup(granted, keys, isLocked)
              const onCount = visible.filter((entry) => granted.has(entry.permission)).length
              // §9.176 - a search overrides the collapse, or the hit is hidden while the screen says nothing matched.
              const isOpen = search.trim() !== '' || opened.has(group.key)
              const movable = keys.some((key) => !isLocked(key))

              return (
                <div
                  key={group.key}
                  className="rounded-2xl border border-border bg-card p-4 space-y-2"
                  data-permission-group={group.key}
                >
                  <div className="flex items-center justify-between gap-2">
                    <button
                      type="button"
                      data-group-toggle={group.key}
                      aria-expanded={isOpen}
                      onClick={() =>
                        setOpened((current) => {
                          const next = new Set(current)
                          if (next.has(group.key)) next.delete(group.key)
                          else next.add(group.key)
                          return next
                        })
                      }
                      className="flex items-center gap-2 min-w-0 flex-1 text-left"
                    >
                      <ChevronRight
                        className={cn('w-4 h-4 shrink-0 text-muted-foreground transition-transform', isOpen && 'rotate-90')}
                      />
                      <h3 className="text-sm font-bold truncate">{group.heading}</h3>
                      {/* A collapsed group that says nothing is a worse wall than an open one. */}
                      <span className="text-[11px] text-muted-foreground tabular-nums shrink-0" data-group-count={group.key}>
                        {formatCount(onCount)} / {formatCount(visible.length)}
                      </span>
                    </button>
                    {/* On the header, where it works on a collapsed group too; it moves only what the reader may. */}
                    {movable && (
                      <button
                        type="button"
                        onClick={() => propose(toggleGroup(granted, keys, isLocked).next)}
                        aria-label={`${allOn ? tt('Vypnúť všetko') : tt('Zapnúť všetko')} - ${group.heading}`}
                        className="shrink-0 px-2.5 py-1 rounded-lg border border-border text-[11px] font-semibold hover:bg-muted/50"
                      >
                        {allOn ? <T>Vypnúť všetko</T> : <T>Zapnúť všetko</T>}
                      </button>
                    )}
                  </div>
                  {isOpen &&
                    visible.map((entry) => (
                      <PermissionRow
                        key={entry.permission}
                        permission={entry.permission}
                        parent={entry.parent}
                        state={switchState(entry.permission, granted, known)}
                        covers={childrenOf(entry.permission, catalogue)}
                        shows={displayChildrenOf(entry.permission, catalogue)}
                        serverChecked={serverChecked.has(entry.permission)}
                        screens={screensOf(entry.permission)}
                        lock={editable ? lockOf(entry.permission) : null}
                        readOnly={!editable}
                        onToggle={() => propose(toggled(granted, entry.permission))}
                      />
                    ))}
                </div>
              )
            })}
          </div>
        </div>

        {dirty ? (
          <PreviewRail
            diff={diff}
            labelOf={labelOf}
            preview={previewState === 'ready' ? answer : null}
            state={previewState}
            onRetryPreview={() => void preview.refetch()}
            saving={save.isPending}
            blocked={blocked}
            onDiscard={() => setLocal(null)}
            onSave={() => setConfirming(true)}
          />
        ) : (
          <p className="hidden lg:block rounded-xl border border-dashed border-border p-3 text-xs text-muted-foreground" data-rail-idle>
            {editable
              ? tt('Zatiaľ nič nemeníte. Keď zapnete alebo vypnete právo, tu uvidíte zmeny a koho sa týkajú.')
              : tt('Túto rolu si môžete len pozrieť.')}
          </p>
        )}
      </div>

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={confirmSave}
        title={`${tt('Uložiť práva pre')} ${name}?`}
        description={confirmText}
        confirmLabel={tt('Uložiť')}
        loading={save.isPending}
      />
    </div>
  )
}

/**
 * Why the save is refused before it is pressed, or null - the rail's own line, in the order a reader can act on it:
 * a role they may not change, a role whose rights somebody moved since the first tick (reload, or the server answers
 * `ROLE_STALE`), then rights the server's preview refuses (it refuses the whole save, naming them).
 */
function blockedReason(editable: boolean, rightsMoved: boolean, refused: number): string | null {
  if (!editable) return tt('Túto rolu nemôžete meniť.')
  if (rightsMoved) return tt('Práva tejto roly medzitým niekto zmenil - najprv ju načítajte znova.')
  if (refused > 0) return tt('Server by zmenu odmietol celú - najprv vráťte práva označené vyššie.')
  return null
}

/**
 * A value that settles before it is acted on - the preview follows the ticks, and ten quick ticks are one question.
 *
 * The timer sets the state from its own callback, never during the effect, so the rail never asks about a set the
 * editor has already moved past.
 */
function useSettled(value: string, delay: number): string {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return settled
}

/**
 * A custom role's name, colour and description - the main administrator's alone (`PATCH /settings/roles/{key}`).
 *
 * **It sends only what the reader changed** (`roleMetaPatch`): it opened on the role's own colour or none, and a rename
 * that carried a colour nobody picked repainted the role's badge on every screen.
 */
function RoleMetaForm({
  role,
  roles,
  onDone,
}: {
  role: RoleView
  roles: readonly RoleView[]
  onDone: () => void
}) {
  const { toast } = useToast()
  const update = useUpdateRole()
  const [name, setName] = useState(role.name ?? '')
  const [color, setColor] = useState<RoleColor | null>(isRoleColor(role.color) ? role.color : null)
  const [description, setDescription] = useState(role.description ?? '')
  const problem = roleNameProblem(name, roles, role.key)

  const submit = () => {
    const patch = roleMetaPatch(role, { name, color, description })
    // Nothing changed is nothing to send - and no row on the trail saying a role was edited when it was not.
    if (Object.keys(patch).length === 0) {
      onDone()
      return
    }
    update.mutate(
      { key: role.key, data: patch },
      {
        onSuccess: () => {
          toast({ title: tt('Rola upravená') })
          onDone()
        },
        onError: (error: unknown) =>
          toast({
            title: tt('Chyba'),
            description: apiErrorMessage(error, tt('Rola sa neupravila.')),
            variant: 'destructive',
          }),
      },
    )
  }

  return (
    <div className="space-y-2 rounded-xl border border-border p-3" data-role-meta-form>
      <label className="block space-y-1">
        <span className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{tt('Názov')}</span>
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={40}
          className="w-full px-3 py-2 rounded-xl bg-background border border-border text-sm"
          data-role-meta-name
        />
      </label>
      {problem !== null && (
        <p className="text-xs text-red-500" data-role-meta-problem={problem}>
          {roleNameProblemText(problem)}
        </p>
      )}
      <ColorPicker value={color} onChange={setColor} />
      <label className="block space-y-1">
        <span className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{tt('Popis')}</span>
        <textarea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          maxLength={200}
          rows={2}
          className="w-full px-3 py-2 rounded-xl bg-background border border-border text-sm"
          data-role-meta-description
        />
      </label>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onDone}
          className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50"
        >
          {tt('Zrušiť')}
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={problem !== null || update.isPending}
          className={cn('px-3 py-1.5 rounded-lg', BUTTON_PRIMARY, 'text-xs')}
          data-role-meta-save
        >
          {tt('Uložiť')}
        </button>
      </div>
    </div>
  )
}

/**
 * The ten colours a role's badge may carry - V89's list, which is `StatusBadge`'s - each drawn and announced by its
 * Slovak name (`roleColorLabel`), never by the key the server stores.
 */
export function ColorPicker({
  value,
  onChange,
}: {
  value: RoleColor | null
  onChange: (color: RoleColor) => void
}) {
  return (
    <div className="space-y-1" data-role-colors>
      <span className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{tt('Farba')}</span>
      <div className="flex flex-wrap gap-1.5">
        {STAFF_ROLE_COLORS.map((color) => (
          <button
            key={color}
            type="button"
            onClick={() => onChange(color)}
            aria-pressed={value === color}
            aria-label={roleColorLabel(color)}
            data-role-color={color}
            className={cn('rounded-full p-0.5 border-2', value === color ? 'border-foreground' : 'border-transparent')}
          >
            <StatusBadge label={roleColorLabel(color)} color={color} />
          </button>
        ))}
      </div>
    </div>
  )
}

function PermissionRow({
  permission,
  parent,
  state,
  covers,
  shows,
  serverChecked,
  screens,
  lock,
  readOnly,
  onToggle,
}: {
  permission: string
  parent: string | null
  state: SwitchState
  covers: string[]
  shows: string[]
  serverChecked: boolean
  /** The sidebar entries this right opens - none for a right no entry is gated on. */
  screens: readonly string[]
  lock: RightLock | null
  readOnly: boolean
  onToggle: () => void
}) {
  // §9.145 - a name for the row, not the slug it is stored under.
  const label = permissionLabel(permission, parent)
  const description = permissionDescription(permission)

  return (
    <div className={cn('flex items-center gap-3', parent !== null && 'pl-6')} data-permission-row={permission}>
      <button
        type="button"
        onClick={onToggle}
        disabled={readOnly || lock !== null}
        aria-label={permission}
        aria-pressed={state === 'on'}
        data-switch-state={state}
        className={cn(
          'w-10 h-5 rounded-full shrink-0 relative transition-colors disabled:opacity-50',
          state === 'on' && 'bg-primary',
          state === 'via-parent' && 'bg-amber-500/50',
          state === 'off' && 'bg-muted',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all',
            state === 'on' ? 'left-[22px]' : 'left-0.5',
          )}
        />
      </button>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold truncate">
          {label}
          {parent === null && covers.length > 0 && (
            <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">
              <T>udeľuje aj</T> {covers.length}
            </span>
          )}
          {parent === null && shows.length > covers.length && (
            <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">
              ({shows.length - covers.length} <T>samostatne</T>)
            </span>
          )}
        </p>
        {/* §9.107 - legacy's own sentence, so granting a right is not done off a slug. */}
        {description !== null && (
          <p className="text-[11px] text-muted-foreground" data-permission-description>
            {description}
          </p>
        )}
        {parent !== null && <p className="text-[10px] text-muted-foreground font-mono truncate">{permission}</p>}
        {/* §9.752 - the screen a tick puts in somebody's menu, by the label the sidebar draws it with. */}
        {screens.length > 0 && (
          <p className="text-[10px] text-muted-foreground truncate" data-permission-screen>
            {tt('Na obrazovke')}:{' '}
            {screens.map((screen, index) => (
              <span key={screen}>
                {index > 0 && ', '}
                <T>{screen}</T>
              </span>
            ))}
          </p>
        )}
        {lock !== null && (
          <p className="flex items-center gap-1 text-[10px] text-muted-foreground" data-right-locked={lock}>
            <Lock className="w-3 h-3" />
            {lockText(lock)}
          </p>
        )}
      </div>
      {/* §9.752 - which ticks the server checks at all; the rest move the menu and the screen. */}
      {serverChecked && (
        <span
          className="text-[10px] font-semibold text-blue-500 shrink-0 flex items-center gap-1"
          data-server-checked
          title={tt('Toto právo kontroluje aj server, nielen menu.')}
        >
          <Server className="w-3 h-3" />
          {tt('server kontroluje')}
        </span>
      )}
      {/* FB-103 - said on the row, not only in the summary. */}
      {state === 'via-parent' && (
        <span className="text-[10px] text-amber-500 shrink-0" data-via-parent-row>
          <T>platí cez</T> {parent}
        </span>
      )}
    </div>
  )
}
